import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { useI18n } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Gallery() {
  const { t } = useI18n();
  const [photos, setPhotos] = useState([]);
  const [openIdx, setOpenIdx] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/gallery`).then((r) => {
      if (!cancelled) {
        const items = (r.data || []).map((p) => ({
          src: p.url.startsWith("http") ? p.url : `${process.env.REACT_APP_BACKEND_URL}${p.url}`,
          alt: "Eternal City View",
          aspect: p.aspect || "landscape",
        }));
        setPhotos(items);
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const close = useCallback(() => setOpenIdx(null), []);
  const prev = useCallback(
    () => setOpenIdx((i) => (i === null ? null : (i - 1 + photos.length) % photos.length)),
    [photos.length]
  );
  const next = useCallback(
    () => setOpenIdx((i) => (i === null ? null : (i + 1) % photos.length)),
    [photos.length]
  );

  useEffect(() => {
    if (openIdx === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [openIdx, close, prev, next]);

  if (photos.length === 0) return null;

  const active = openIdx !== null ? photos[openIdx] : null;

  return (
    <section
      id="gallery"
      className="py-24 sm:py-32 bg-[#1F1A14] text-[#FAF9F6] relative grain"
      data-testid="gallery-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        <div className="grid md:grid-cols-12 gap-10 mb-16">
          <div className="md:col-span-4">
            <p className="overline text-[#C85A3C]">{t.gallery.overline}</p>
          </div>
          <div className="md:col-span-8">
            <h2 className="font-serif-display text-5xl md:text-6xl leading-[1.05] whitespace-pre-line">
              {t.gallery.title}
            </h2>
            <p className="mt-6 text-[#FAF9F6]/70 text-lg leading-relaxed max-w-2xl">
              {t.gallery.sub}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 auto-rows-[180px] sm:auto-rows-[220px] gap-3 sm:gap-4">
          {photos.map((p, i) => (
            <button
              key={p.src}
              type="button"
              data-testid={`gallery-thumb-${i}`}
              onClick={() => setOpenIdx(i)}
              className={`group relative overflow-hidden bg-[#FAF9F6]/5 focus:outline-none focus:ring-2 focus:ring-[#C85A3C] ${
                p.aspect === "portrait" ? "row-span-2" : "row-span-1"
              }`}
              aria-label={p.alt}
            >
              <img
                src={p.src}
                alt={p.alt}
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-[1.05]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#14110D]/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            </button>
          ))}
        </div>
      </div>

      {active && (
        <div
          className="fixed inset-0 z-[100] bg-[#14110D]/95 backdrop-blur-sm flex items-center justify-center px-4 sm:px-8 py-16"
          onClick={close}
          data-testid="gallery-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={active.alt}
        >
          <button
            type="button"
            onClick={close}
            data-testid="gallery-close"
            aria-label={t.gallery.close}
            className="absolute top-4 right-4 sm:top-6 sm:right-6 w-11 h-11 flex items-center justify-center rounded-full border border-[#FAF9F6]/30 text-[#FAF9F6] hover:border-[#C85A3C] hover:text-[#C85A3C] transition bg-[#14110D]/60"
          >
            <X className="w-5 h-5" />
          </button>

          {photos.length > 1 && (
            <>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); prev(); }}
                data-testid="gallery-prev"
                aria-label={t.gallery.prev}
                className="absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 w-11 h-11 sm:w-12 sm:h-12 flex items-center justify-center rounded-full border border-[#FAF9F6]/30 text-[#FAF9F6] hover:border-[#C85A3C] hover:text-[#C85A3C] transition bg-[#14110D]/60"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); next(); }}
                data-testid="gallery-next"
                aria-label={t.gallery.next}
                className="absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 w-11 h-11 sm:w-12 sm:h-12 flex items-center justify-center rounded-full border border-[#FAF9F6]/30 text-[#FAF9F6] hover:border-[#C85A3C] hover:text-[#C85A3C] transition bg-[#14110D]/60"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </>
          )}

          <div className="max-w-6xl max-h-full flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
            <img
              src={active.src}
              alt={active.alt}
              className="max-w-full max-h-[80vh] object-contain shadow-2xl"
              data-testid="gallery-lightbox-image"
            />
            <p className="mt-4 text-xs sm:text-sm text-[#FAF9F6]/60 tracking-wide text-center max-w-xl">
              {active.alt}
            </p>
            <p className="mt-2 text-[10px] tracking-widest uppercase text-[#FAF9F6]/40">
              {openIdx + 1} / {photos.length}
            </p>
          </div>
        </div>
      )}
    </section>
  );
}
